import { useState } from 'react';
import {
  dialogCounts,
  directionArrow,
  fmtDialogTime,
  foldDialog,
  outboundLead,
  replyChip,
  statusTone,
  type ChipTone,
  type DialogText,
} from '@lib/sms-dialog';

/**
 * The member's texts in the ONE shared layout (Frontend G's "SMS Dialog Shared Layout", 24 Sep 2026
 * 1:14 PM, after ClariConnect's Live Activity Feed "By member" mock): one line per text on three
 * columns (arrow 22 px, time 118 px, the text), oldest first; an outbound line leads with
 * "template_key -> STATUS" (FAILED / REJECTED set apart), the words boxed; a reply boxed with a green
 * border and the feed's chip; the newest six lines open and "N earlier lines" for the rest. Read only.
 */

const CHIP_CLASS: Record<ChipTone, string> = {
  kit: 'pill pill-success',
  decline: 'pill pill-warning',
  stop: 'pill pill-danger',
  help: 'pill',
  other: 'pill',
};

export function SmsDialog({ lines }: { lines: readonly DialogText[] }) {
  const [open, setOpen] = useState(false);
  const { shown, hidden } = foldDialog(lines, open);
  const counts = dialogCounts(lines);
  return (
    <div data-testid="sms-dialog">
      <div className="micro muted" style={{ marginBottom: 6 }}>
        {counts.out} out, {counts.in} in
      </div>
      {hidden > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} style={{ color: 'var(--ink-900)', marginBottom: 4 }} data-testid="sms-dialog-earlier">
          {'▸'} {hidden} earlier line{hidden === 1 ? '' : 's'}
        </button>
      )}
      {shown.map((t) => {
        const inbound = t.direction === 'in';
        const chip = inbound ? replyChip(t) : null;
        const lead = inbound ? null : outboundLead(t);
        const fail = !inbound && statusTone(t.status) === 'fail';
        return (
          <div
            key={t.message_id}
            data-testid={`sms-line-${t.message_id}`}
            style={{ display: 'grid', gridTemplateColumns: '22px 118px 1fr', gap: 8, alignItems: 'start', padding: '4px 0' }}
          >
            <span aria-label={inbound ? 'reply in' : 'text out'} style={{ color: inbound ? 'var(--success, #15803d)' : 'var(--primary, #1d4ed8)' }}>
              {directionArrow(t.direction)}
            </span>
            <span className="small muted" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDialogTime(t.at)}</span>
            <div>
              {lead && (
                <div className="micro muted">
                  {lead.what} {'->'}{' '}
                  <b style={fail ? { color: 'var(--crimson, #991b1b)' } : undefined}>{lead.status}</b>
                </div>
              )}
              <div
                style={{
                  borderRadius: 6,
                  padding: '6px 8px',
                  background: inbound ? 'var(--green-bg, #ecfdf3)' : 'var(--paper-2, #f3f5f9)',
                  border: `1px solid ${inbound ? 'var(--success, #15803d)' : 'var(--rule, #d6d9e0)'}`,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {t.text}
                {chip && (
                  <span className={CHIP_CLASS[chip.tone]} style={{ marginLeft: 8 }} data-testid="sms-chip">
                    {chip.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {lines.length === 0 && <div className="small muted">No texts on record for this member.</div>}
    </div>
  );
}
